import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import CloseIcon from "@mui/icons-material/Close"
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Box,
    Grid,
    IconButton,
    Typography,
} from "@mui/material"
import ButtonWithIcon from "src/v2/components/Button/Button"
import DynamicTextarea from "src/v2/components/Input/DynamicTextarea"
import InputAutoComplete from "src/v2/components/Input/Input-auto-complete"
import TextFieldInput from "src/v2/components/Input/text-field-input"
import SelectComponent from "src/v2/components/select/select-component"
import { ReactComponent as CheckIcon } from "src/v2/assets/icons/check-icon.svg"
import theme from "src/v2/theme/theme"
import { getRootCnpj } from "src/v2/utils/cnpj-root"
import {
    COMPANY_CREATE_REQUIRED_FIELDS,
    isValidCpfCnpj,
    useCompanyCreate,
} from "./use-company-create"
import CompanyBulkImport from "./company-bulk-import"
import {
    booleanOptions,
    filterEconomicGroupOptions,
    getBooleanSelectValue,
    getEconomicGroupOptions,
    groupFields,
    initialValues,
    isEconomicGroupOptionEqualToValue,
    resolveEconomicGroup,
    toBooleanOrNull,
} from "./company-create-config"

const SectionTitle = ({ id, children }) => (
    <Typography
        id={id}
        component="h2"
        variant="subtitle1"
        sx={{ color: "icon.white70" }}
    >
        {children}
    </Typography>
)

SectionTitle.propTypes = {
    id: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
}

const CompanyCreateModal = ({ open, onClose, onCreated, companies, companiesLoading }) => {
    const [values, setValues] = useState(initialValues)
    const [subsectors, setSubsectors] = useState([])
    const [errors, setErrors] = useState({})
    const [economicGroupInputValue, setEconomicGroupInputValue] = useState("")
    const subsectorRequestRef = useRef(0)
    const {
        submitting,
        periodicities,
        sectors,
        loadSubsectors,
        createCompany,
    } = useCompanyCreate({
        onCreated,
        onValidationError: (fieldErrors) => setErrors((currentErrors) => ({ ...currentErrors, ...fieldErrors })),
    })
    const economicGroupOptions = useMemo(() => getEconomicGroupOptions(companies), [companies])

    useEffect(() => {
        if (!open) return

        setValues(initialValues)
        setSubsectors([])
        setErrors({})
        setEconomicGroupInputValue("")
    }, [open])

    useEffect(() => {
        if (!open || values.PeriodicidadeBalanco !== "") return

        const annualPeriodicity = periodicities.find(
            (option) => String(option.label ?? "").trim().toLowerCase() === "anual"
        )

        if (!annualPeriodicity) return

        setValues((currentValues) => currentValues.PeriodicidadeBalanco === ""
            ? { ...currentValues, PeriodicidadeBalanco: annualPeriodicity.value }
            : currentValues
        )
    }, [open, periodicities, values.PeriodicidadeBalanco])

    const updateValue = (name, value) => {
        setValues((currentValues) => ({ ...currentValues, [name]: value }))
        setErrors((currentErrors) => ({ ...currentErrors, [name]: undefined }))
    }

    const handleSectorChange = async (event) => {
        const sector = event.target.value
        const requestId = subsectorRequestRef.current + 1
        subsectorRequestRef.current = requestId

        updateValue("Setor", sector)
        setValues((currentValues) => ({ ...currentValues, SubSetor: "" }))
        setSubsectors([])

        const options = await loadSubsectors(sector)
        if (subsectorRequestRef.current === requestId) setSubsectors(options)
    }

    const handleEconomicGroupChange = (_event, option) => {
        const resolved = resolveEconomicGroup(option, economicGroupOptions)

        setValues((currentValues) => ({
            ...currentValues,
            GrupoEconomico_Nome: resolved.grupoEconomico,
            GrupoEconomico_RaizCNPJ: resolved.grupoEconomicoRaizCNPJ,
        }))
        setErrors((currentErrors) => ({
            ...currentErrors,
            GrupoEconomico_Nome: undefined,
            GrupoEconomico_RaizCNPJ: undefined,
        }))
        setEconomicGroupInputValue(resolved.displayLabel)
    }

    const handleEconomicGroupInputChange = (_event, inputValue, reason) => {
        if (reason === "reset") return

        const nextInputValue = inputValue ?? ""
        const resolved = resolveEconomicGroup(nextInputValue, economicGroupOptions)

        setEconomicGroupInputValue(resolved.displayLabel)
        setValues((currentValues) => ({
            ...currentValues,
            GrupoEconomico_Nome: nextInputValue ? resolved.grupoEconomico : "",
            GrupoEconomico_RaizCNPJ: nextInputValue ? resolved.grupoEconomicoRaizCNPJ : "",
        }))
        setErrors((currentErrors) => ({
            ...currentErrors,
            GrupoEconomico_Nome: undefined,
        }))
    }

    const validate = () => {
        const nextErrors = COMPANY_CREATE_REQUIRED_FIELDS.reduce((currentErrors, field) => (
            String(values[field] ?? "").trim()
                ? currentErrors
                : { ...currentErrors, [field]: "Este campo é obrigatório." }
        ), {})

        if (values.CPFCNPJ && !isValidCpfCnpj(values.CPFCNPJ)) {
            nextErrors.CPFCNPJ = "Informe um CPF ou CNPJ válido."
        }

        setErrors(nextErrors)
        return Object.keys(nextErrors).length === 0
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        if (!validate()) return

        const created = await createCompany(values)
        if (created) onClose()
    }

    const handleImported = useCallback(async () => {
        await onCreated?.()
        onClose()
    }, [onClose, onCreated])

    const handleClose = () => {
        if (!submitting) onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.common.white,
                    m: { xxxs: 1, sm: 2 },
                    width: { xxxs: "calc(100vw - 16px)", sm: "calc(100vw - 32px)" },
                },
            }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Typography component="div" variant="h3">Cadastrar Empresa</Typography>
                <IconButton
                    aria-label="Fechar cadastro de empresa"
                    onClick={handleClose}
                    disabled={submitting}
                    sx={{
                        color: "icon.main",
                        "&:focus-visible": {
                            outline: `2px solid ${theme.palette.common.white}`,
                            outlineOffset: 2,
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: theme.palette.input.borderColor.primary }}>
                <Box component="form" id="company-create-form" onSubmit={handleSubmit}>
                    <Grid container spacing={2} sx={{ pt: 0.5 }}>
                    <Grid item xxxs={12}>
                        <SectionTitle id="company-main-data-title">Dados cadastrais</SectionTitle>
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <TextFieldInput
                            fullWidth
                            required
                            name="CPFCNPJ"
                            label="CPF/CNPJ"
                            value={values.CPFCNPJ}
                            onChange={(event) => updateValue("CPFCNPJ", event.target.value)}
                            error={Boolean(errors.CPFCNPJ)}
                            helperText={errors.CPFCNPJ}
                        />
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <TextFieldInput
                            fullWidth
                            name="RaizCNPJ"
                            label="Raiz CNPJ"
                            value={getRootCnpj(values.CPFCNPJ)}
                            InputProps={{ readOnly: true }}
                            error={Boolean(errors.RaizCNPJ)}
                            helperText={errors.RaizCNPJ}
                        />
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <TextFieldInput
                            fullWidth
                            required
                            name="RazaoSocial"
                            label="Razão social"
                            value={values.RazaoSocial}
                            onChange={(event) => updateValue("RazaoSocial", event.target.value)}
                            error={Boolean(errors.RazaoSocial)}
                            helperText={errors.RazaoSocial}
                        />
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <TextFieldInput
                            fullWidth
                            required
                            name="NomeFantasia"
                            label="Nome fantasia"
                            value={values.NomeFantasia}
                            onChange={(event) => updateValue("NomeFantasia", event.target.value)}
                            error={Boolean(errors.NomeFantasia)}
                            helperText={errors.NomeFantasia}
                        />
                    </Grid>
                    <Grid item xxxs={12} sx={{ pt: "24px !important" }}>
                        <Divider sx={{ mb: 3, borderColor: "transparent.white10" }} />
                        <SectionTitle id="company-complementary-data-title">Informações complementares</SectionTitle>
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <InputAutoComplete
                            fullWidth
                            label="Grupo econômico"
                            value={economicGroupInputValue}
                            inputValue={economicGroupInputValue}
                            options={economicGroupOptions}
                            loading={companiesLoading}
                            loadingText="Carregando grupos econômicos..."
                            noOptionsText="Nenhum grupo econômico encontrado"
                            isOptionEqualToValue={isEconomicGroupOptionEqualToValue}
                            filterOptions={(options, state) => filterEconomicGroupOptions(options, state, economicGroupInputValue)}
                            onChange={handleEconomicGroupChange}
                            onInputChange={handleEconomicGroupInputChange}
                            inputVariant="form"
                            forcePopupIcon
                            freeSolo
                            error={Boolean(errors.GrupoEconomico_Nome)}
                            helperText={errors.GrupoEconomico_Nome}
                        />
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <TextFieldInput
                            fullWidth
                            name="GrupoEconomico_RaizCNPJ"
                            label="Raiz CNPJ Grupo Econômico"
                            value={values.GrupoEconomico_RaizCNPJ}
                            onChange={(event) => updateValue("GrupoEconomico_RaizCNPJ", event.target.value)}
                            error={Boolean(errors.GrupoEconomico_RaizCNPJ)}
                            helperText={errors.GrupoEconomico_RaizCNPJ}
                        />
                    </Grid>
                    <Grid item xxxs={12} sx={{ pt: "24px !important" }}>
                        <Divider sx={{ mb: 3, borderColor: "transparent.white10" }} />
                        <SectionTitle id="company-description-title">Descrição da empresa</SectionTitle>
                    </Grid>
                    <Grid item xxxs={12}>
                        <TextFieldInput
                            fullWidth
                            name="Site_RI"
                            label="Site RI"
                            value={values.Site_RI}
                            onChange={(event) => updateValue("Site_RI", event.target.value)}
                            error={Boolean(errors.Site_RI)}
                            helperText={errors.Site_RI}
                        />
                    </Grid>
                    <Grid item xxxs={12}>
                        <DynamicTextarea
                            name="Observacao"
                            label="Observação"
                            value={values.Observacao}
                            onChange={(event) => updateValue("Observacao", event.target.value)}
                            characterLimit={300}
                            error={Boolean(errors.Observacao)}
                            helperText={errors.Observacao}
                        />
                    </Grid>
                    <Grid item xxxs={12}>
                        <DynamicTextarea
                            name="Atividade"
                            label="Atividade"
                            value={values.Atividade}
                            onChange={(event) => updateValue("Atividade", event.target.value)}
                            characterLimit={600}
                            error={Boolean(errors.Atividade)}
                            helperText={errors.Atividade}
                        />
                    </Grid>
                    <Grid item xxxs={12} sx={{ pt: "24px !important" }}>
                        <Divider sx={{ mb: 3, borderColor: "transparent.white10" }} />
                        <SectionTitle id="company-classification-title">Classificação</SectionTitle>
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <SelectComponent
                            required
                            name="Setor"
                            label="Setor *"
                            value={values.Setor}
                            options={sectors}
                            onChange={handleSectorChange}
                            error={Boolean(errors.Setor)}
                            helperText={errors.Setor}
                        />
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <SelectComponent
                            required
                            name="SubSetor"
                            label="Subsetor *"
                            value={values.SubSetor}
                            options={subsectors}
                            onChange={(event) => updateValue("SubSetor", event.target.value)}
                            error={Boolean(errors.SubSetor)}
                            helperText={errors.SubSetor}
                            disabled={!values.Setor}
                        />
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <SelectComponent
                            name="TipoCadastro"
                            label="Tipo de cadastro"
                            value={values.TipoCadastro}
                            options={[{ value: "MATRIZ", label: "MATRIZ" }, { value: "FILIAL", label: "FILIAL" }]}
                            onChange={(event) => updateValue("TipoCadastro", event.target.value)}
                            error={Boolean(errors.TipoCadastro)}
                            helperText={errors.TipoCadastro}
                        />
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <SelectComponent
                            name="PeriodicidadeBalanco"
                            label="Periodicidade do balanço"
                            value={values.PeriodicidadeBalanco}
                            options={periodicities}
                            onChange={(event) => updateValue("PeriodicidadeBalanco", event.target.value)}
                            error={Boolean(errors.PeriodicidadeBalanco)}
                            helperText={errors.PeriodicidadeBalanco}
                        />
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <TextFieldInput
                            fullWidth
                            name="MesFechamento"
                            type="number"
                            label="Mês de fechamento"
                            value={values.MesFechamento}
                            inputProps={{ min: 1, max: 12 }}
                            onChange={(event) => updateValue("MesFechamento", event.target.value)}
                            error={Boolean(errors.MesFechamento)}
                            helperText={errors.MesFechamento}
                        />
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <SelectComponent
                            name="EGEM"
                            label="EGEM"
                            value={getBooleanSelectValue(values.EGEM)}
                            options={booleanOptions}
                            onChange={(event) => updateValue("EGEM", toBooleanOrNull(event.target.value))}
                            clearable
                            clearAriaLabel="Limpar EGEM"
                            onClear={() => updateValue("EGEM", null)}
                            error={Boolean(errors.EGEM)}
                            helperText={errors.EGEM}
                        />
                    </Grid>
                    <Grid item xxxs={12} md={6}>
                        <SelectComponent
                            name="EFRF"
                            label="EFRF"
                            value={getBooleanSelectValue(values.EFRF)}
                            options={booleanOptions}
                            onChange={(event) => updateValue("EFRF", toBooleanOrNull(event.target.value))}
                            clearable
                            clearAriaLabel="Limpar EFRF"
                            onClear={() => updateValue("EFRF", null)}
                            error={Boolean(errors.EFRF)}
                            helperText={errors.EFRF}
                        />
                    </Grid>
                    <Grid item xxxs={12} sx={{ pt: "24px !important" }}>
                        <Divider sx={{ mb: 3, borderColor: "transparent.white10" }} />
                        <SectionTitle id="company-groups-title">Grupos relacionados</SectionTitle>
                    </Grid>
                    {groupFields.map((field) => (
                        <Grid item xxxs={12} md={6} key={field.name}>
                            <TextFieldInput
                                fullWidth
                                type="number"
                                name={field.name}
                                label={field.label}
                                value={values[field.name]}
                                inputProps={{ min: 0 }}
                                onChange={(event) => updateValue(field.name, event.target.value)}
                                error={Boolean(errors[field.name])}
                                helperText={errors[field.name]}
                            />
                        </Grid>
                    ))}
                    </Grid>
                    <Divider sx={{ mt: 3, borderColor: "transparent.white10" }} />
                    <CompanyBulkImport onImported={handleImported} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <ButtonWithIcon
                    type="submit"
                    form="company-create-form"
                    size="md"
                    icon={<CheckIcon />}
                    disabled={submitting}
                >
                    {submitting ? "CADASTRANDO..." : "CADASTRAR"}
                </ButtonWithIcon>
            </DialogActions>
        </Dialog>
    )
}

CompanyCreateModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func,
    companies: PropTypes.arrayOf(PropTypes.shape({
        NomeFantasia: PropTypes.string,
        RazaoSocial: PropTypes.string,
        GrupoEconomico_Nome: PropTypes.string,
        GrupoEconomico_RaizCNPJ: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        CPFCNPJ: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        RaizCNPJ: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
    companiesLoading: PropTypes.bool,
}

CompanyCreateModal.defaultProps = {
    onCreated: null,
    companies: [],
    companiesLoading: false,
}

export default CompanyCreateModal
